import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { FileSizePipe, SharedModule } from '@aiwyn/shared';
import { CxpRelativeTimePipe } from '../../../../shared';
import { CrlActivityEntry, CrlActivityFile } from '../../../../models';

/**
 * Activity feed (CRL redesign, Figma node 12595-20668). Renders the mapped
 * `CrlActivityEntry[]` for the selected item: a 32px pastel initial-avatar, an
 * actor + action line (linked object names in teal), an optional file chip
 * (preview + download icon), an optional comment box (normal, or the critical
 * red "requested changes" box), and a muted relative timestamp.
 *
 * Presentational: entries are fully composed by the bridge mapper + selector.
 * File preview/download are bubbled to the container (which owns the real
 * `FilePreviewService` wire). Deferred this pass: unread dots. Uses the CXP
 * `cxpRelativeTime` pipe for timestamps and the shared `fileSize` pipe for chip
 * sizes. Loading + empty states are explicit.
 *
 * A restricted item's feed is masked SERVER-side (per-row `entry.isMasked`, with
 * content sent as "......" and null file ids), so it renders like any other feed
 * — except masked chips are inert, since preview/download could never resolve.
 */
@Component({
  selector: 'cxp-crl-panel-activity',
  templateUrl: './crl-panel-activity.component.html',
  styleUrls: ['./crl-panel-activity.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, FileSizePipe, CxpRelativeTimePipe],
})
export class CrlPanelActivityComponent {
  /** Mapped feed entries for the selected item. */
  readonly activity = input<CrlActivityEntry[]>([]);
  /** True while the item's activity is being fetched (shows a loading line). */
  readonly isLoading = input<boolean>(false);
  /** Hide the internal "Activity" heading when a parent already titles the block. */
  readonly showTitle = input<boolean>(true);

  /** Preview an activity file chip (chip body click). */
  readonly previewFile = output<CrlActivityFile>();
  /** Download an activity file chip (download icon click). */
  readonly downloadFile = output<CrlActivityFile>();
}
